import React, { useMemo } from 'react';
import { Alert, SimpleGrid, Stack, Text } from '@mantine/core';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { IconAlertTriangle } from '@tabler/icons-react';
import KpiPage, {
  ChartCard,
  ChartTooltip,
  QueryState,
  StatTile,
  fmtMoney,
  fmtPct,
  useChartColors,
} from '../KpiPage';
import { kpiBySlug } from '../kpiCatalog';
import { useWindowParams } from '../DateWindowSelector';
import useNexusQuery, { field, num } from '../../../hooks/useNexusQuery';

const kpi = kpiBySlug('managed-vs-unmanaged-segments');

const RESELLER = 'Reseller';
const END_USER = 'End User / Operator';

export default function ManagedVsUnmanagedSegments() {
  const c = useChartColors();
  const [window, setWindow] = useWindowParams();

  const data = useNexusQuery('initiative-managed-by-segment', window);

  const rows = useMemo(
    () =>
      data.rows.map((r) => ({
        channel: field(r, 'Channel'),
        freshness: field(r, 'Freshness'),
        keyCustomer: field(r, 'KeyCustomer'),
        revenue: num(r, 'Revenue'),
        margin: num(r, 'GrossMargin'),
      })),
    [data.rows]
  );

  // Fresh (NEW/OH/SV) is the population this KPI is about. AR is core material — selling that to
  // a reseller is ordinary business, so folding it in would dilute the very thing being measured.
  const byChannel = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const e = map.get(r.channel) ?? { channel: r.channel, fresh: 0, asRemoved: 0, margin: 0 };
      if (r.freshness === 'Fresh') e.fresh += r.revenue;
      else e.asRemoved += r.revenue;
      e.margin += r.margin;
      map.set(r.channel, e);
    });
    return [...map.values()]
      .map((e) => ({
        ...e,
        total: e.fresh + e.asRemoved,
        marginPct: e.fresh + e.asRemoved ? (e.margin / (e.fresh + e.asRemoved)) * 100 : 0,
      }))
      .sort((a, b) => b.fresh - a.fresh);
  }, [rows]);

  const summary = useMemo(() => {
    const fresh = rows.filter((r) => r.freshness === 'Fresh');
    const freshTotal = fresh.reduce((s, r) => s + r.revenue, 0);
    const toReseller = fresh
      .filter((r) => r.channel === RESELLER)
      .reduce((s, r) => s + r.revenue, 0);
    const toEndUser = fresh.filter((r) => r.channel === END_USER).reduce((s, r) => s + r.revenue, 0);
    const keyRev = rows.filter((r) => r.keyCustomer === 'Key').reduce((s, r) => s + r.revenue, 0);
    const allRev = rows.reduce((s, r) => s + r.revenue, 0);
    const marginSpread = byChannel.length
      ? Math.max(...byChannel.map((b) => b.marginPct)) -
        Math.min(...byChannel.map((b) => b.marginPct))
      : 0;
    return {
      freshTotal,
      toReseller,
      toEndUser,
      resellerShare: freshTotal ? (toReseller / freshTotal) * 100 : 0,
      endUserShare: freshTotal ? (toEndUser / freshTotal) * 100 : 0,
      keyShare: allRev ? (keyRev / allRev) * 100 : 0,
      marginSpread,
    };
  }, [rows, byChannel]);

  const channelColor = (ch) =>
    ch === RESELLER ? c.negative : ch === END_USER ? c.positive : c.series[0];

  return (
    <KpiPage kpi={kpi} window={window} onWindowChange={setWindow} hideMethodology>
      <Stack gap="md">
        <QueryState isLoading={data.isLoading} error={data.error} isEmpty={!rows.length}>
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
            <StatTile
              label="SV/OH/New A/B to resellers"
              value={fmtPct(summary.resellerShare)}
              sub={`${fmtMoney(summary.toReseller)} — the leak`}
              color={c.negative}
            />
            <StatTile
              label="SV/OH/New A/B to end users"
              value={fmtPct(summary.endUserShare)}
              sub={`${fmtMoney(summary.toEndUser)} placed directly`}
              color={c.positive}
            />
            <StatTile
              label="Revenue to key accounts"
              value={fmtPct(summary.keyShare)}
              sub="All Class A/B, key vs non-key"
            />
            <StatTile
              label="Margin spread across channels"
              value={`${summary.marginSpread.toFixed(1)} pts`}
              sub="How much better direct actually pays"
              color={summary.marginSpread < 5 ? c.series[3] : c.positive}
            />
          </SimpleGrid>
        </QueryState>

        {/* The uncomfortable finding. Stating it here stops the KPI being used to argue something
            the data does not currently support. */}
        {summary.marginSpread > 0 && summary.marginSpread < 5 && (
          <Alert
            color="orange"
            variant="light"
            icon={<IconAlertTriangle size={18} />}
            title="Margin alone does not make the case"
          >
            <Text size="sm">
              Realized margin is nearly flat across channels — about{' '}
              {summary.marginSpread.toFixed(1)} points between best and worst. On today&apos;s
              numbers we are not capturing a price premium for selling direct, so the argument for
              shifting volume away from resellers has to rest on repeat business, relationship and
              lifetime value rather than on unit margin. It may also mean we are discounting to end
              users as hard as we discount to brokers — which would be a pricing-discipline problem
              wearing a channel-mix costume.
            </Text>
          </Alert>
        )}

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <ChartCard
            title="Where SV/OH/New Class A/B parts go"
            caption="Sellable-condition premium inventory by destination. Resellers outranking end users is the problem this KPI exists to make visible — every one of those units is a part a broker now sells to an operator instead of us."
            height={320}
          >
            <QueryState isLoading={data.isLoading} error={data.error} isEmpty={!byChannel.length}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byChannel} margin={{ top: 8, right: 8, left: 0, bottom: 50 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                  <XAxis
                    dataKey="channel"
                    stroke={c.axis}
                    fontSize={10}
                    angle={-25}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis
                    stroke={c.axis}
                    fontSize={12}
                    tickFormatter={(v) => fmtMoney(v)}
                    width={56}
                  />
                  <Tooltip
                    content={<ChartTooltip formatter={fmtMoney} />}
                    cursor={{ fill: c.grid, opacity: 0.3 }}
                    wrapperStyle={{ outline: 'none' }}
                  />
                  <Bar dataKey="fresh" name="SV / OH / New" radius={[4, 4, 0, 0]}>
                    {byChannel.map((b) => (
                      <Cell key={b.channel} fill={channelColor(b.channel)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>

          <ChartCard
            title="SV/OH/New vs as-removed, margin by channel"
            caption="As-removed material going to resellers is normal trade, so it is shown separately. Read the margin line against the bars: if it stays flat, channel choice is not currently changing what we realize."
            height={320}
          >
            <QueryState isLoading={data.isLoading} error={data.error} isEmpty={!byChannel.length}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={byChannel} margin={{ top: 8, right: 8, left: 0, bottom: 50 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                  <XAxis
                    dataKey="channel"
                    stroke={c.axis}
                    fontSize={10}
                    angle={-25}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis
                    yAxisId="left"
                    stroke={c.axis}
                    fontSize={12}
                    tickFormatter={(v) => fmtMoney(v)}
                    width={56}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke={c.axis}
                    fontSize={12}
                    tickFormatter={(v) => `${Math.round(v)}%`}
                    width={44}
                    domain={[0, 'dataMax + 10']}
                  />
                  <Tooltip
                    content={
                      <ChartTooltip
                        formatter={(v, e) => (e.dataKey === 'marginPct' ? fmtPct(v) : fmtMoney(v))}
                      />
                    }
                    cursor={{ fill: c.grid, opacity: 0.3 }}
                    wrapperStyle={{ outline: 'none' }}
                  />
                  <Legend iconType="circle" verticalAlign="top" />
                  <Bar
                    yAxisId="left"
                    dataKey="fresh"
                    name="SV / OH / New"
                    stackId="a"
                    fill={c.series[0]}
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="asRemoved"
                    name="As removed"
                    stackId="a"
                    fill={c.neutral}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="marginPct"
                    name="Margin rate"
                    stroke={c.series[3]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>
        </SimpleGrid>
      </Stack>
    </KpiPage>
  );
}
